import { useTeam } from '@/hooks/useTeam'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Loading from '@/components/common/Loading'
import DraftNotes from '@/components/dashboard/DraftNotes'
import TaskWidget from '@/components/dashboard/TaskWidget'
import DispatchHistory from '@/components/dashboard/DispatchHistory'

function Dashboard() {
  const { team, loading, hasTeam } = useTeam()
  const { user, loading: authLoading, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [portalSettings, setPortalSettings] = useState(null)
  const [restoredFormState, setRestoredFormState] = useState(null)
  const [pendingSuggestionsCount, setPendingSuggestionsCount] = useState(0)

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth?tab=signin')
  }

  useEffect(() => {
    // Don't redirect while auth or team is loading
    if (authLoading || loading) {
      return
    }
    
    // Only redirect if we're sure there's no team
    if (!hasTeam) {
      navigate('/team-setup')
    }
  }, [loading, hasTeam, authLoading, user?.id, navigate])

  useEffect(() => {
    const fetchPendingSuggestions = async () => {
      if (!team?.id) return

      try {
        const { count, error } = await supabase
          .from('wiki_suggestions')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', team.id)
          .eq('status', 'pending')

        if (!error) {
          setPendingSuggestionsCount(count || 0)
        }
      } catch (err) {
        console.error('Failed to fetch pending suggestions count:', err)
      }
    }

    fetchPendingSuggestions()
  }, [team?.id])

  useEffect(() => {
    if (!team?.id) return

    const fetchPortalSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('portal_settings')
          .select('*')
          .eq('team_id', team.id)
          .maybeSingle()

        if (error) throw error
        if (data) {
          setPortalSettings(data)
        }
      } catch (err) {
        console.error('Error fetching portal settings:', err)
      }
    }

    fetchPortalSettings()
  }, [team?.id])

  // Restore form state when returning from review page
  useEffect(() => {
    if (location.state?.formState) {
      setRestoredFormState(location.state.formState)
      // Clear the state to prevent re-applying on future navigations
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  if (loading || authLoading) {
    return <Loading text="Loading dashboard..." />
  }

  if (!team) {
    return null
  }

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">
              {team.name}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-brand-slate">
                Portal: benchbulletin.com/p/{team.url_slug}
              </p>
              <Link
                to={`/p/${team.url_slug}`}
                className="text-brand-green text-sm hover:underline"
              >
                View Portal →
              </Link>
              <span className="text-brand-slate">•</span>
              <Link
                to="/scrimmages/bulletin/hockey"
                className="text-brand-green text-sm hover:underline"
              >
                Scrimmage Board →
              </Link>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="secondary"
              onClick={() => navigate('/parents')}
            >
              Manage Parents
            </Button>
            <Button 
              variant="secondary"
              onClick={() => navigate('/settings')}
            >
              Settings
            </Button>
            <Button 
              variant="secondary"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Quick Actions - Full Width */}
        <div className="mb-8">
          <Card>
            <h2 className="font-heading text-xl font-bold mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <button 
                  onClick={() => navigate('/parents')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                >
                  <div className="font-medium text-brand-charcoal mb-1">
                    Parent Emails
                  </div>
                  <div className="text-sm text-brand-slate">
                    Manage your parent list
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/portal/edit')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition relative"
                >
                  <div className="font-medium text-brand-charcoal mb-1 flex items-center gap-2">
                    Edit Portal {team.season_tier !== 'paid' && '🔒'}
                    {pendingSuggestionsCount > 0 && (
                      <Badge variant="primary" className="text-xs">
                        {pendingSuggestionsCount} new
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-brand-slate">
                    Customize your public portal page
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/files')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                >
                  <div className="font-medium text-brand-charcoal mb-1">
                    Team Files
                  </div>
                  <div className="text-sm text-brand-slate">
                    Upload and share files
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/roster')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                >
                  <div className="font-medium text-brand-charcoal mb-1">
                    Team Roster {team.season_tier !== 'paid' && '🔒'}
                  </div>
                  <div className="text-sm text-brand-slate">
                    Manage player roster
                  </div>
                </button>
                {(portalSettings === null || portalSettings?.snacks_public === true) && (
                  <button 
                    onClick={() => navigate('/snacks')}
                    className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                  >
                    <div className="font-medium text-brand-charcoal mb-1">
                      Snack Schedule {team.season_tier !== 'paid' && '🔒'}
                    </div>
                    <div className="text-sm text-brand-slate">
                      Assign snack duties
                    </div>
                  </button>
                )}
                <button 
                  onClick={() => navigate('/settings')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                >
                  <div className="font-medium text-brand-charcoal mb-1">
                    Settings
                  </div>
                  <div className="text-sm text-brand-slate">
                    Team & portal privacy
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/scrimmages/manage')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:bg-brand-cream/40 transition"
                >
                  <div className="font-medium text-brand-charcoal mb-1">
                    Scrimmage Manager {team.season_tier !== 'paid' && '🔒'}
                  </div>
                  <div className="text-sm text-brand-slate">
                    Post & find scrimmages
                  </div>
                </button>
              </div>
            </Card>
        </div>

        {/* Main Content Area - Draft Notes, Recent Dispatches, and Tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Draft Notes - 2 columns on large screens */}
          <div className="lg:col-span-2">
            <DraftNotes team={team} restoredFormState={restoredFormState} />
          </div>

          {/* Sidebar - Tasks and Recent Dispatches */}
          <div className="lg:col-span-1 space-y-6">
            <TaskWidget team={team} />
            <DispatchHistory team={team} />
          </div>
        </div>
      </Container>
    </div>
  )
}

export default Dashboard

